import { useCallback, useEffect, useState, type ReactNode } from "react";

import type { PaymentInstrument, RiderApi } from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import {
  ChevronControl,
  EmptyRow,
  Group,
  MarkControl,
  MarketShell,
  type BadgeValue,
  type RowValue,
} from "./MarketShell.tsx";
import { MarketState } from "./TrancheState.tsx";

/**
 * `Payment methods` — PAY-04's masked instruments, and the four designed rows beside them.
 *
 * | Drawn                               | From                                                      |
 * | ----------------------------------- | --------------------------------------------------------- |
 * | `M-Pesa ··· 0421`                   | PAY-04's `provider` and `maskedAccount`                   |
 * | `Default · verified`                | PAY-04's `isDefault` and `verification`                   |
 * | `Airtel Money ··· 7710`             | the same two fields on the next instrument                |
 * | `Added 12 Aug`                      | PAY-04's `registeredAt`                                   |
 * | `M-Pesa is your default`            | the default instrument's own provider                     |
 * | `Visa ··· 4417` / `Expires 08/28`   | **nothing** — no service stores a card instrument         |
 * | `Bank transfer` / `Cash` and their sub-lines | designed copy: these are method types, not records |
 * | `Add mobile money` and `●` / `○`    | published commands with **no designed form** — Q-144      |
 *
 * ## The screen the measurement had never been able to grade anything but blocked
 *
 * `tests/contract/mobile-screen-support.test.ts` carried one sentence about this screen for weeks:
 * _"A stored payment instrument. Six screens draw `M-Pesa ··· 0421`; no service models one."_
 * Measured, that was exact. PAY-04 published two operations and both were executions.
 *
 * [ADR 0042](../../../docs/adr/0042-a-masked-mobile-money-instrument-is-pay-04s-record-and-only-its-mask-is-published.md)
 * gives PAY-04 the record, and the shape of what it publishes is the whole point rather than a
 * precaution: six fields, every one of them drawn, and the subscriber's full account never leaves the
 * service on either surface. **`maskedAccount` arrives as `··· 0421` — elision included — and this
 * file shortens nothing.** That is deliberate: for a browser to shorten an account, the account would
 * have had to reach the browser, and then the full value has crossed the wire whatever is rendered.
 * So the row's title is `${provider} ${maskedAccount}` and the decision about how much of an account
 * a rider sees belongs to the service that owns the account.
 *
 * ## Two designed controls, two published commands, and no designed form for either
 *
 * This is the one thing on this screen that is knowingly not finished, and it is a **design** gap
 * rather than a platform one, which is why the buttons are drawn and explain themselves rather than
 * being left out.
 *
 * `14 WP13 Money and Account.dc.html` draws `Add mobile money` in the footer, `add` in the app bar,
 * and a `●` or `○` on every row — and draws **no form, anywhere, for any of them**. There is no
 * screen in that file, or in `SCREEN_SPEC.md`, where a rider types a mobile-money number.
 * `POST /v1/tenants/{tenantId}/mobile-money/instruments` exists and takes a provider and an account
 * reference, so the platform is ready and this application has nothing to send.
 *
 * `CLAUDE.md` section 1 settles what to do about that: _"Don't add anything that isn't in the
 * design."_ A number-entry sheet on a payment screen is not a detail — it is an unapproved screen on
 * a money path, and inventing one is exactly the kind of wrong answer that gets built on. So the
 * controls are drawn as drawn, pressing one says what is missing, and **Q-144** records the question
 * with this as what was done meanwhile. Changing which instrument is the default is a second,
 * narrower case of the same gap: ADR 0042 derives the default from being first rather than publishing
 * a command to move it, so the `●` and `○` have no route either.
 *
 * ## The `Other` group, which is three rows and one absence
 *
 * The design's second group draws a stored card, a bank transfer and cash. Only the first of those is
 * a **record**:
 *
 *   - `Visa ··· 4417` with `Expires 08/28` is an instance of something, and nothing publishes it.
 *     PAY-05 has three operations and all three are card authorizations under `/internal/v1`; there
 *     is no stored card anywhere on the platform. Both lines are drawn as the absence rather than
 *     omitted, because `CLAUDE.md` section 1 forbids removing a row as firmly as adding one. The row
 *     keeps its `credit_card` glyph and its `○`, so the group is the shape the design draws.
 *   - `Bank transfer` and `Cash` are **method types** rather than instances: there is one bank
 *     transfer and one cash, and their labels and sub-lines are designed copy. They are drawn word
 *     for word. `Bank transfer`'s `set up` pill has no route behind it — PAY-06 publishes two
 *     internal operations and neither registers anything — so it explains itself when pressed.
 *
 * ## What the chip says when there is nothing to be default
 *
 * The design's chip is `done · M-Pesa is your default`, which is the default instrument's own
 * provider. A rider with no instruments has no default, and the design draws no chip for that state —
 * so the chip takes the bundle's `idle` kind and wording this build is responsible for, flagged under
 * the sub-line by `MarketShell`'s own `flag` affordance rather than passed off as approved copy.
 */

export interface PaymentMethodsProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly tenantId: string;
  readonly onBack: () => void;
}

/** Why `Add mobile money`, the app bar's `add` and every row's mark cannot be pressed. */
const NO_FORM =
  "The command exists (POST /v1/tenants/{tenantId}/mobile-money/instruments, ADR 0042) and " +
  "14 WP13 Money and Account.dc.html draws no form for entering a mobile-money number, so this " +
  "build has nothing to send. Recorded as Q-144 rather than invented.";

/** Why a row's `●` or `○` cannot move the default. */
const NO_DEFAULT_COMMAND =
  "Nothing publishes a command to change which instrument is the default. ADR 0042 derives it from " +
  "being the first one registered. Q-144.";

/** Why `Bank transfer`'s `set up` pill cannot be pressed. */
const NO_BANK_SETUP =
  "PAY-06 publishes two operations and both are bank-rail executions under /internal/v1; nothing " +
  "registers a bank instrument.";

/** Why `Visa ··· 4417` and `Expires 08/28` have no source. */
const NO_STORED_CARD =
  "No service stores a card instrument: PAY-05's three operations are card authorizations under " +
  "/internal/v1, and nothing publishes a card's mask or its expiry";

export function PaymentMethods(props: PaymentMethodsProps): ReactNode {
  const { api, tenantId } = props;

  const [instruments, setInstruments] = useState<readonly PaymentInstrument[]>([]);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const load = useCallback(() => {
    setState({ kind: "loading" });
    api
      .paymentInstruments(tenantId)
      .then((answer) => {
        setInstruments(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => setState(stateForError(error, [SCOPES.paymentInstruments])));
  }, [api, tenantId]);

  useEffect(load, [load]);

  if (state.kind !== "ready") {
    return (
      <MarketState
        state={state}
        onRetry={load}
        onBack={props.onBack}
        purpose="Reading how you pay."
        barTitle="Payment methods"
        barSub="Account"
      />
    );
  }

  const settled = instruments.find((instrument) => instrument.isDefault);

  return (
    <MarketShell
      screen="methods"
      left={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      barTitle="Payment methods"
      barSub="Account"
      right={{ glyph: "add", label: "Add mobile money", unavailable: NO_FORM }}
      badge={
        settled === undefined
          ? { kind: "idle", label: "no default yet" }
          : { kind: "done", label: `${settled.provider} is your default` }
      }
      title="How you pay"
      sub="Mobile money first. Card, bank and cash too."
      {...(settled === undefined
        ? { flag: "wording not in the design: no instrument is the default" }
        : {})}
      actions={[{ label: "Add mobile money", glyph: "add", primary: true, unavailable: NO_FORM }]}
    >
      <Group
        heading="Mobile money"
        rows={instruments.map(rowFor)}
        empty={
          <EmptyRow words="No mobile-money account is registered yet. Add mobile money needs a number-entry form the design does not draw (Q-144)" />
        }
      />

      {/*
       * The design's second group, in its own order: the stored card, the bank transfer, then cash.
       *
       * Built as a literal rather than from data because two of the three *are* literals — a method
       * type rather than an instance — and the third is an absence. See this file's header.
       */}
      <Group heading="Other" rows={OTHER_ROWS} />
    </MarketShell>
  );
}

/**
 * One instrument as the design writes a row: a glyph, the provider and its mask, a sub-line, a
 * badge and a mark.
 *
 * The sub-line is the design's own two cases rather than a generalisation of them. `M-Pesa ··· 0421`
 * reads `Default · verified` and `Airtel Money ··· 7710` reads `Added 12 Aug`, so the rule is: the
 * default says that it is and whether it is verified, and the others say when they were added. Both
 * of the design's rows come out exactly as written.
 *
 * `verification` is drawn as the word PAY-04 publishes. `unverified` and `rejected` are states the
 * design draws no copy for — it draws the default as `verified` and nothing else — so they appear as
 * the service's own word rather than as a sentence this build made up about a payment instrument.
 */
function rowFor(instrument: PaymentInstrument): RowValue {
  const badge: BadgeValue | undefined = instrument.isDefault
    ? { kind: "done", label: "default" }
    : undefined;
  return {
    id: instrument.instrumentId,
    // `smartphone` on both of the design's mobile-money rows, whichever provider they name.
    lead: "smartphone",
    title: `${instrument.provider} ${instrument.maskedAccount}`,
    sub: instrument.isDefault
      ? `Default · ${instrument.verification}`
      : `Added ${addedOn(instrument.registeredAt)}`,
    ...(badge === undefined ? {} : { badge }),
    control: (
      <MarkControl
        on={instrument.isDefault}
        label={
          instrument.isDefault
            ? `${instrument.provider} ${instrument.maskedAccount} is your default`
            : `Make ${instrument.provider} ${instrument.maskedAccount} your default`
        }
        unavailable={NO_DEFAULT_COMMAND}
      />
    ),
  };
}

/**
 * `12 Aug` — the design's own date form for `Added 12 Aug`.
 *
 * Day then abbreviated month, no year, which is what that row writes. A date in another year would
 * read the same, which is a real limitation of the design's form rather than of this function; the
 * design draws one sample and it is in the current year.
 */
function addedOn(at: string): string {
  return new Date(at).toLocaleDateString([], { day: "numeric", month: "short" });
}

/**
 * The three rows of the design's `Other` group.
 *
 * A module constant rather than a function of anything, because nothing it draws depends on data:
 * the card is an absence and the other two are method types the design names. It is above the
 * component rather than inside it so it is built once.
 */
const OTHER_ROWS: readonly RowValue[] = [
  {
    id: "card",
    lead: "credit_card",
    title: "Visa ··· 4417",
    titleAbsent: NO_STORED_CARD,
    sub: "Expires 08/28",
    subAbsent: NO_STORED_CARD,
    control: <MarkControl on={false} label="Pay by card" unavailable={NO_STORED_CARD} />,
  },
  {
    id: "bank",
    lead: "account_balance",
    title: "Bank transfer",
    sub: "For business accounts",
    badge: { kind: "idle", label: "set up" },
    control: <ChevronControl label="Set up bank transfer" unavailable={NO_BANK_SETUP} />,
  },
  {
    id: "cash",
    lead: "payments",
    title: "Cash",
    sub: "Pay the driver",
    /*
     * `○` and not `●`, which is what the design draws — cash is not the default in the sample.
     *
     * Whether cash is selected is not a record anywhere: a rider pays the driver or they do not, and
     * no service publishes a standing preference for it. So the mark is drawn in the design's own
     * state and explains itself, rather than being wired to something that would have had to be
     * invented to wire it to.
     */
    control: (
      <MarkControl
        on={false}
        label="Pay with cash"
        unavailable="Nothing publishes a standing preference for paying with cash"
      />
    ),
  },
];
